/**
 * Affiliate Tracking Module
 *
 * Automatically tracks affiliate clicks when URL contains affiliate code parameter.
 * Detects the parameter name from window.RUNTIME_CONFIG.global_affiliate_code_query_param_name
 * and sends tracking data to backend via AJAX.
 *
 * Features:
 * - Silent failure (no user notification on errors)
 * - Automatic device detection
 * - Tracks landing URL, referrer, and user agent
 * - Stores click token in cookie for conversion tracking
 * - Cookie name configurable via .env (CLICK_TRACKING_COOKIE_NAME)
 */
class AffiliateTracking {
  constructor() {
    this.paramName = null;
    this.affCode = null;
    this.cookieName = null;
    this.init();
  }

  /**
   * Initialize the tracking module
   */
  init() {
    // Check if affiliate tracking is enabled
    if (!this.isAffiliateEnabled()) {
      return;
    }

    // Get cookie name from runtime config
    this.cookieName = this.getCookieName();

    // Get the query parameter name from runtime config
    this.paramName = this.getQueryParamName();
    if (!this.paramName) {
      console.warn("[AffiliateTracking] Query parameter name not configured");
      return;
    }

    // Check if URL contains affiliate code
    this.affCode = this.getAffiliateCodeFromUrl();
    if (!this.affCode) {
      // No affiliate code in URL, nothing to track
      return;
    }

    // Track the affiliate click
    this.trackClick();
  }

  /**
   * Check if affiliate tracking is enabled in runtime config
   * @returns {boolean}
   */
  isAffiliateEnabled() {
    return (
      window.RUNTIME_CONFIG &&
      window.RUNTIME_CONFIG.global_affiliate_store_front_enable === 1
    );
  }

  /**
   * Get the click tracking cookie name from runtime config
   * @returns {string}
   */
  getCookieName() {
    if (
      window.RUNTIME_CONFIG &&
      window.RUNTIME_CONFIG.global_click_tracking_cookie_name
    ) {
      return window.RUNTIME_CONFIG.global_click_tracking_cookie_name;
    }
    // Fallback to default
    return "OVCT";
  }

  /**
   * Get the affiliate code query parameter name from runtime config
   * @returns {string|null}
   */
  getQueryParamName() {
    if (
      window.RUNTIME_CONFIG &&
      window.RUNTIME_CONFIG.global_affiliate_code_query_param_name
    ) {
      return window.RUNTIME_CONFIG.global_affiliate_code_query_param_name;
    }
    return null;
  }

  /**
   * Extract affiliate code from URL query parameters
   * @returns {string|null}
   */
  getAffiliateCodeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get(this.paramName);
    return code ? code.trim() : null;
  }

  /**
   * Detect device type (mobile or desktop)
   * @returns {string}
   */
  detectDevice() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check for mobile devices
    if (
      /android/i.test(userAgent) ||
      /iPhone|iPad|iPod/i.test(userAgent) ||
      /BlackBerry|BB|PlayBook/i.test(userAgent) ||
      /Windows Phone/i.test(userAgent) ||
      /Opera Mini/i.test(userAgent) ||
      /Mobile|mobile/i.test(userAgent)
    ) {
      return "mobile";
    }

    return "desktop";
  }

  /**
   * Track the affiliate click by sending data to backend
   */
  trackClick() {
    const trackingData = {
      aff_code: this.affCode,
      landing_url: window.location.href,
      referrer: document.referrer || "",
      user_agent: navigator.userAgent || "",
      device: this.detectDevice(),
    };

    // Send tracking request
    fetch("/affiliatetracking/trackajax", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(trackingData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Optionally dispatch a custom event for other modules to listen
          this.dispatchTrackingEvent(data);
        } else {
          // Silent failure - just log to console
          console.warn("[AffiliateTracking] Tracking failed:", data.message);
        }
      })
      .catch((error) => {
        // Silent failure - just log to console
        console.error("[AffiliateTracking] Error tracking click:", error);
      });
  }

  /**
   * Dispatch custom event when tracking is successful
   * Other modules can listen to this event if needed
   * @param {Object} data - Response data from tracking API
   */
  dispatchTrackingEvent(data) {
    const event = new CustomEvent("affiliateClickTracked", {
      detail: {
        affCode: this.affCode,
        clickToken: data.data?.click_token,
        trackingData: data.data,
        cookieName: this.cookieName,
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Get the click tracking cookie value
   * Helper method for other modules that need to access the click token
   * @returns {string|null}
   */
  getClickToken() {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === this.cookieName) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }
}

export default AffiliateTracking;
